/* eslint-disable no-underscore-dangle */
/**
 *
 * Overlay
 *
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useWindowSize from 'components/useWindowSize';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import ArrowRight from '@material-ui/icons/KeyboardArrowRight';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import useContentfulLocale from 'components/useContentfulLocale';
import { useSelector } from 'react-redux';
import { makeSelectSiteCopyInOtherLang } from 'containers/Main/selectors';
import { DEFAULT_PRESENTATION } from 'utils/constants';
import CloseIcon from '@material-ui/icons/Close';
import { IconButton } from '@material-ui/core';
import AssessmentItemSmall from 'components/AssessmentItemSmall';
import SuggestionPill from './SuggestionPill';
import TopicItem from './TopicItem';
import CrisisBanner from './CrisisBanner';
import ResourceItem from './ResourceItem';
import useFocusTrap from '../hooks/useFocusTrap';
import { ExclusiveResources } from './ExclusiveResources';

const useStyles = makeStyles(theme => ({
  searchPopover: {
    zIndex: 9999,
    position: 'fixed !important',
    width: 'fit-content',
    '&[x-placement="bottom"]': {
      [theme.breakpoints.up('sm')]: {
        left: props =>
          props.alignTo === 'left'
            ? `${props.leftOffset}px !important`
            : 'auto !important',
        right: props =>
          props.alignTo === 'right'
            ? `${props.rightOffset}px !important`
            : 'auto !important',
        transform: props =>
          `translate3d(0, ${props.offsetTop + 50}px, 0px) !important`,
      },
      [theme.breakpoints.down('xs')]: {
        transform: 'translate3d(0, 80px, 0) !important',
      },
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  searchMobilePopover: {
    marginTop: 0,
    [theme.breakpoints.down('600')]: {
      marginTop: 53,
      minWidth: 250,
    },
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    width: 'auto',
    background: '#FFFFFF',
    borderRadius: 4,
    overflowY: 'scroll',
    maxHeight: props => `calc(100vh - ${props.offsetTop + 63}px)`,
    overflowX: 'hidden',
    boxShadow: '0 12px 40px 0 rgba(134,130,130,0.4)',
    [theme.breakpoints.down('xs')]: {
      boxShadow: 'none',
      width: '100%',
      height: 'calc(100vh - 80px)',
      flexGrow: '1',
    },
  },
  header: {
    width: props => props.hasOnlyOneType && 400,
    position: 'relative',
    alignItems: 'center',
    padding: props => `${props.hasOnlyOneType ? 12 : 0}px 40px`,
    marginBottom: 0,
    marginTop: 12,
    [theme.breakpoints.down('xs')]: {
      padding: '0 5% !important',
      height: 'fit-content',
    },
  },
  resourcesContainer: {
    width: 340,
  },
  topicsContainer: {
    width: props => (props.hasAssessments ? 240 : 350),
  },
  assessmentContainer: {
    width: 250,
  },
  closeIcon: {
    position: 'absolute',
    top: '-100px',
    right: '20px',
    padding: 0,
    outline: 'none !important',
    '&:focus': {
      top: '8px',
    },
  },
  resultsLabel: {
    fontFamily: 'PoppinsBold',
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '16px',
    color: '#212121',
    '& > span': {
      color: theme.palette.text.secondary,
    },
  },
  searchSuggestions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    marginLeft: 20,
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
      height: 'fit-content',
      overflowY: 'hidden',
      flexWrap: 'wrap',
      minWidth: 0,
      marginLeft: 0,
    },
  },
  searchSuggestionsOnly: {
    justifyContent: 'flex-start',
  },
  searchSuggestionsLabel: {
    fontSize: 14,
    lineHeight: '14px',
    marginRight: 8,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginBottom: 8,
    },
  },
  resultsContainer: {
    display: 'flex',
    padding: '24px 40px',
    margin: 0,
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      padding: '16px 5%',
      flexDirection: 'column',
      gap: 16,
      whiteSpace: 'inital',
      flex: '1',
    },
  },
  oneTypeSuggestions: {
    margin: 0,
    flexWrap: 'wrap',
    width: 330,
    padding: '0 !important',
    gap: 8,
    justifyContent: 'flex-start',
  },
  oneTypeSuggestionPill: {
    margin: 0,
  },
  oneTypeResultsLabel: {
    marginRight: 16,
  },
  listHeader: {
    ...theme.typography.h7,
    fontSize: 13,
    color: '#212121',
    marginBottom: 12,
  },
  linkContainer: {
    display: 'flex',
    padding: '8px 0',
    justifyContent: 'center',
    backgroundColor: '#EEF9FF',
    [theme.breakpoints.down('xs')]: {
      padding: '16px 0',
    },
  },
  item: {
    padding: '4px 8px !important',
  },
  link: {
    textDecoration: 'none',
    fontSize: 13,
    lineHeight: '21px',
    '& > svg': {
      fontSize: 16,
    },
  },
  assessment: {
    [theme.breakpoints.down('xs')]: {
      width: 200,
    },
  },
  resultsLabelContainer: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}));

const Overlay = props => {
  const {
    anchorEl,
    data,
    focused,
    offsetTop,
    onChange,
    onClickAway,
    onLinkClick,
    onResourceClick,
    onSuggestionPillClick,
    query,
    searchConfig,
    isEmbedded,
    section,
    isVariantE,
    variantELocale,
    otherLanguage,
    focusableElements,
  } = props;
  const hasExclusive = !_isEmpty(data.exclusive);
  const hasTopics = !_isEmpty(data.topics);
  const hasAssessments = !_isEmpty(data.assessments);
  const clientDetails = useSelector(state => state.main.clientDetails);
  const hasResources = !_isEmpty(data.resources);
  const hasCrisisFlag = data.crisisFlag;
  const { width } = useWindowSize();
  const isDesktop = width > 900;
  const hasOnlyOneType =
    (!hasExclusive || data?.exclusive?.length === 1) &&
    [hasTopics, hasAssessments, hasResources].filter(Boolean).length === 1;
  const finalOffset = isEmbedded ? -23 : offsetTop;

  const isSeparateLogoLayout =
    _get(clientDetails, 'metadata.separateLogoLayout', false) ||
    _get(clientDetails, 'logosCollection.items', []).length > 2;

  const positionProps = useMemo(() => {
    const inputEl = document.getElementById('nav-menu-search-input');
    const alignmentOffset = 300;

    if (!inputEl) {
      return { alignTo: 'right', rightOffset: 20, leftOffset: 'auto' };
    }

    const inputRect = inputEl.getBoundingClientRect();
    const screenMidpoint = width / 2;
    if (inputRect.left + alignmentOffset > screenMidpoint) {
      return {
        alignTo: 'right',
        rightOffset: width - inputRect.right,
      };
    }

    return {
      alignTo: 'left',
      leftOffset: inputRect.left,
    };
  }, [width]);

  const classes = useStyles({
    offsetTop: finalOffset,
    hasAssessments,
    hasOnlyOneType,
    isSeparateLogoLayout,
    ...positionProps,
  });
  const ctfLocaleFilter = useContentfulLocale();
  const siteCopyInOtherLang = useSelector(makeSelectSiteCopyInOtherLang());

  const focusNextElement = () => {
    const lastElement = focusableElements?.[focusableElements?.length - 1];
    if (!lastElement?.current) return;

    const allFocusableElements = Array.from(
      document.querySelectorAll(
        'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    );

    const currentIndex = allFocusableElements.indexOf(lastElement.current);

    if (currentIndex !== -1 && currentIndex + 1 < allFocusableElements.length) {
      allFocusableElements[currentIndex + 1].focus();
    }
  };

  const handleEscape = () => {
    onClickAway();
    focusNextElement();
  };

  const tooltipRef = useFocusTrap({
    focusableElements,
    onEscape: handleEscape,
  });

  const renderCloseButton = () => (
    <IconButton
      className={classes.closeIcon}
      onClick={handleEscape}
      aria-label="Close"
    >
      <CloseIcon />
    </IconButton>
  );

  const searchSuggestionsContent = (
    <Grid container spacing={2} className={classes.header}>
      {renderCloseButton()}
      <Grid
        item
        xs
        className={_classNames(
          classes.searchSuggestions,
          classes.searchSuggestionsOnly,
        )}
      >
        <Typography
          color="textPrimary"
          className={classes.searchSuggestionsLabel}
        >
          {_get(searchConfig, 'pageHeader', 'Top Searches')}
        </Typography>
        {_get(searchConfig, 'pageCopy.search', []).map(text => (
          <SuggestionPill
            hasIcon
            key={`suggestion-${text}`}
            text={text}
            onClick={() => onSuggestionPillClick(text)}
          />
        ))}
      </Grid>
    </Grid>
  );

  const renderHeaderRightPart = () => {
    if (hasCrisisFlag) {
      return (
        <CrisisBanner
          query={query}
          fromHeader={section === 'header'}
          onClose={onClickAway}
        />
      );
    }

    if (hasOnlyOneType && isDesktop) {
      return (
        <Grid
          item
          xs
          className={_classNames(
            classes.searchSuggestions,
            classes.oneTypeSuggestions,
          )}
        >
          <Typography
            color="textPrimary"
            className={_classNames(
              classes.resultsLabel,
              classes.oneTypeResultsLabel,
            )}
          >
            {_get(searchConfig, 'pageCopy.resultsLabel')} <span>“{query}”</span>
          </Typography>
          <Typography
            color="textPrimary"
            className={classes.searchSuggestionsLabel}
          >
            {_get(searchConfig, 'pageCopy.otherLabel')}
          </Typography>
          {_get(searchConfig, 'pageCopy.search', [])
            .filter(
              suggestion => suggestion.toLowerCase() !== query.toLowerCase(),
            )
            .slice(0, 4)
            .map(text => (
              <SuggestionPill
                key={`suggestion-${text}`}
                text={text}
                className={classes.oneTypeSuggestionPill}
                onClick={() => onChange(text)}
              />
            ))}
        </Grid>
      );
    }

    if (!hasAssessments && !hasTopics && !hasResources) {
      return (
        <Grid item xs className={classes.searchSuggestions}>
          <Typography
            color="textPrimary"
            className={classes.searchSuggestionsLabel}
          >
            {_get(searchConfig, 'pageCopy.otherLabel')}
          </Typography>
          {_get(searchConfig, 'pageCopy.search', [])
            .filter(
              suggestion => suggestion.toLowerCase() !== query.toLowerCase(),
            )
            .slice(0, 4)
            .map(text => (
              <SuggestionPill
                key={`suggestion-${text}`}
                text={text}
                onClick={() => onChange(text)}
              />
            ))}
        </Grid>
      );
    }

    return null;
  };

  const content = useMemo(() => {
    const assessment = data?.assessments?.[0];

    return _isEmpty(data) ? null : (
      <>
        <Grid container spacing={2} className={classes.header}>
          {renderCloseButton()}
          {!(hasOnlyOneType && isDesktop) && (
            <Grid item className={classes.resultsLabelContainer}>
              <Typography color="textPrimary" className={classes.resultsLabel}>
                {_get(searchConfig, 'pageCopy.resultsLabel')}{' '}
                <span>“{query}”</span>
              </Typography>
            </Grid>
          )}
          {renderHeaderRightPart()}
        </Grid>
        {hasExclusive && (
          <ExclusiveResources
            onLinkClick={onLinkClick}
            searchConfig={searchConfig}
            data={data.exclusive}
            onResourceClick={onResourceClick}
            variantELocale={variantELocale}
            otherLanguage={otherLanguage}
            siteCopyInOtherLang={siteCopyInOtherLang}
          />
        )}
        <Box className={classes.resultsContainer}>
          {hasAssessments && (
            <Box className={classes.assessmentContainer}>
              <Typography className={classes.listHeader}>
                {_get(searchConfig, 'pageCopy.assessmentsLabel')}
              </Typography>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  key={`${assessment.url}-${assessment.slug}`}
                  className={classes.item}
                >
                  <AssessmentItemSmall
                    onClick={onResourceClick}
                    item={assessment}
                    customClasses={{ root: classes.assessment }}
                    fromSuggestions
                  />
                </Grid>
                <Grid item xs={12}>
                  <Link
                    to={`/assessments?lang=${variantELocale ||
                      ctfLocaleFilter.locale}`}
                    className={classes.link}
                    onClick={() => onLinkClick('Assessments')}
                  >
                    {_get(searchConfig, 'pageCopy.assessmentsViewAll')}{' '}
                    <ArrowRight />
                  </Link>
                </Grid>
              </Grid>
            </Box>
          )}
          {hasTopics && (
            <Box className={classes.topicsContainer}>
              <Typography className={classes.listHeader}>
                {_get(searchConfig, 'pageCopy.topicsLabel')}
              </Typography>
              <Grid container spacing={2}>
                {data.topics.slice(0, 2).map(item => (
                  <Grid
                    item
                    xs={12}
                    key={`${item.url}-${item.slug}`}
                    className={classes.item}
                  >
                    <TopicItem
                      data={item}
                      onClick={onResourceClick}
                      variantELocale={variantELocale}
                      topicSource="search"
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Link
                    to={`/topics?lang=${variantELocale ||
                      ctfLocaleFilter.locale}`}
                    className={classes.link}
                    onClick={() => onLinkClick('Topics')}
                  >
                    {_get(searchConfig, 'pageCopy.topicsViewAll')}{' '}
                    <ArrowRight />
                  </Link>
                </Grid>
              </Grid>
            </Box>
          )}
          {hasResources && (
            <Box className={classes.resourcesContainer}>
              <Typography className={classes.listHeader}>
                {_get(searchConfig, 'pageCopy.matchingResourcesLabel')}
              </Typography>
              <Grid container spacing={2}>
                {data.resources.slice(0, 3).map(item => (
                  <Grid
                    item
                    xs={12}
                    key={`${item.url}-${item.slug}`}
                    className={classes.item}
                  >
                    <ResourceItem
                      data={item}
                      onClick={onResourceClick}
                      variantELocale={variantELocale}
                      siteCopyInOtherLang={otherLanguage && siteCopyInOtherLang}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Link
                    to={`/resources?query=${query}&presentation=${DEFAULT_PRESENTATION}${
                      variantELocale ? `&lang=${variantELocale}` : ''
                    }`}
                    className={classes.link}
                    onClick={() => onLinkClick('Resources')}
                  >
                    {_get(searchConfig, 'pageCopy.resourcesAll')}
                    <ArrowRight />
                  </Link>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
        <Box className={classes.linkContainer}>
          <Link
            to={`/resources?query=${query}&presentation=${DEFAULT_PRESENTATION}${
              variantELocale ? `&lang=${variantELocale}` : ''
            }`}
            className={classes.link}
            onClick={() => onLinkClick('Resources')}
          >
            {_get(searchConfig, 'pageCopy.resourcesViewAll')} “{query}”{' '}
            <ArrowRight />
          </Link>
        </Box>
      </>
    );
  }, [data]);

  return (
    <Popper
      open={focused}
      anchorEl={anchorEl}
      className={_classNames(
        classes.searchPopover,
        classes.searchMobilePopover,
      )}
      ref={tooltipRef}
      role="presentation"
      placement="bottom"
      modifiers={{
        flip: {
          enabled: false,
        },
        preventOverflow: {
          enabled: true,
          padding: isVariantE && 40,
          boundariesElement: 'window',
        },
      }}
    >
      <ClickAwayListener mouseEvent="onMouseDown" onClickAway={onClickAway}>
        <div className={classes.paper}>
          {_isEmpty(data) ? searchSuggestionsContent : content}
        </div>
      </ClickAwayListener>
    </Popper>
  );
};

Overlay.propTypes = {
  anchorEl: PropTypes.element,
  data: PropTypes.object,
  focused: PropTypes.bool,
  offsetTop: PropTypes.number,
  onChange: PropTypes.func,
  onClickAway: PropTypes.func,
  onLinkClick: PropTypes.func,
  onResourceClick: PropTypes.func,
  onSuggestionPillClick: PropTypes.func,
  query: PropTypes.string,
  searchConfig: PropTypes.object,
  isEmbedded: PropTypes.bool,
  isVariantE: PropTypes.bool,
  variantELocale: PropTypes.string,
  otherLanguage: PropTypes.bool,
  focusableElements: PropTypes.array,
};

Overlay.defaultProps = {
  isVariantE: false,
};

export default Overlay;
